const segment = (value: string) => encodeURIComponent(value);
const project = (organizationId: string, projectId: string) =>
	`/v1/orgs/${segment(organizationId)}/projects/${segment(projectId)}`;

export const ENDPOINTS = {
	manifest: () => "/v1/contract-manifest",
	login: () => "/v1/auth/login",
	refresh: () => "/v1/auth/refresh",
	signup: () => "/v1/auth/signup",
	me: () => "/v1/me",
	userAttributes: (userId: string) => `/v1/users/${segment(userId)}/attributes`,
	organizationProjects: (organizationId: string) => `/v1/orgs/${segment(organizationId)}/projects`,
	organizationWorkspaceTypes: (organizationId: string) =>
		`/v1/orgs/${segment(organizationId)}/workspace-types`,
	organizationProject: (organizationId: string, projectId: string) =>
		project(organizationId, projectId),
	organizationProjectRestore: (organizationId: string, projectId: string) =>
		`${project(organizationId, projectId)}/restore`,
	organizationRepositories: (organizationId: string) =>
		`/v1/orgs/${segment(organizationId)}/repositories`,
	projectRepositories: (organizationId: string, projectId: string) =>
		`${project(organizationId, projectId)}/repositories`,
	projectRepository: (organizationId: string, projectId: string, repositoryId: string) =>
		`${project(organizationId, projectId)}/repositories/${segment(repositoryId)}`,
	/** Zerovia 0.3.35 — Project-mapped Gitea repos (ensure + list). */
	projectGitRepos: (organizationId: string, projectId: string) =>
		`${project(organizationId, projectId)}/git/repos`,
	catalog: (organizationId: string, projectId: string) =>
		`${project(organizationId, projectId)}/catalog`,
	dashboard: (organizationId: string, projectId: string) =>
		`${project(organizationId, projectId)}/dashboard`,
	providers: (organizationId: string, projectId: string) =>
		`${project(organizationId, projectId)}/providers`,
	authenticationProfiles: (organizationId: string, projectId: string) =>
		`${project(organizationId, projectId)}/authentication-profiles`,
	authenticationProfileTest: (organizationId: string, projectId: string, profileId: string) =>
		`${project(organizationId, projectId)}/authentication-profiles/${segment(profileId)}/test`,
	authenticationProfileStatus: (organizationId: string, projectId: string, profileId: string) =>
		`${project(organizationId, projectId)}/authentication-profiles/${segment(profileId)}/status`,
	sessions: (organizationId: string, projectId: string) =>
		`${project(organizationId, projectId)}/sessions`,
	session: (organizationId: string, projectId: string, sessionId: string) =>
		`${project(organizationId, projectId)}/sessions/${segment(sessionId)}`,
	sessionChat: (organizationId: string, projectId: string, sessionId: string) =>
		`${project(organizationId, projectId)}/sessions/${segment(sessionId)}/chat`,
	executions: (organizationId: string, projectId: string) =>
		`${project(organizationId, projectId)}/executions`,
	executionTimeline: (organizationId: string, projectId: string, executionId: string) =>
		`${project(organizationId, projectId)}/executions/${segment(executionId)}/timeline`,
	executionCommand: (
		organizationId: string,
		projectId: string,
		executionId: string,
		command: string,
	) =>
		`${project(organizationId, projectId)}/executions/${segment(executionId)}/${segment(command)}`,
	executionEvents: (organizationId: string, projectId: string, executionId: string) =>
		`${project(organizationId, projectId)}/executions/${segment(executionId)}/events`,
	waitHandleResolve: (organizationId: string, projectId: string, waitHandleId: string) =>
		`${project(organizationId, projectId)}/wait-handles/${segment(waitHandleId)}/resolve`,
	waitHandleReissueToken: (organizationId: string, projectId: string, waitHandleId: string) =>
		`${project(organizationId, projectId)}/wait-handles/${segment(waitHandleId)}/reissue-token`,
	artifacts: (organizationId: string, projectId: string) =>
		`${project(organizationId, projectId)}/artifacts`,
	artifact: (organizationId: string, projectId: string, artifactId: string) =>
		`${project(organizationId, projectId)}/artifacts/${segment(artifactId)}`,
	artifactContent: (organizationId: string, projectId: string, artifactId: string) =>
		`${project(organizationId, projectId)}/artifacts/${segment(artifactId)}/content`,
	artifactDownload: (organizationId: string, projectId: string, artifactId: string) =>
		`${project(organizationId, projectId)}/artifacts/${segment(artifactId)}/download`,
	costs: (organizationId: string, projectId: string) =>
		`${project(organizationId, projectId)}/costs`,
	audit: (organizationId: string, projectId: string) =>
		`${project(organizationId, projectId)}/audit`,
	llmUsageSummary: () => "/v1/llm-usage-summary",
	platformProviders: () => "/api/provider/v1/providers",
	systemAuthPools: () => "/api/provider/v1/system-auth-pools",
	systemAuthAssignments: () => "/api/provider/v1/system-auth-assignments",
	workOrders: (organizationId: string, projectId: string) =>
		`${project(organizationId, projectId)}/orders`,
	workOrder: (organizationId: string, projectId: string, orderId: string) =>
		`${project(organizationId, projectId)}/orders/${segment(orderId)}`,
	workOrderTransitions: (organizationId: string, projectId: string, orderId: string) =>
		`${project(organizationId, projectId)}/orders/${segment(orderId)}/transitions`,
	workOrderRestore: (organizationId: string, projectId: string, orderId: string) =>
		`${project(organizationId, projectId)}/orders/${segment(orderId)}/restore`,
	workOrderProjectSettings: (organizationId: string, projectId: string) =>
		`${project(organizationId, projectId)}/orders/settings`,
	workOrderRetry: (organizationId: string, projectId: string, orderId: string) =>
		`${project(organizationId, projectId)}/orders/${segment(orderId)}/retry`,
	workOrderQuestions: (organizationId: string, projectId: string, orderId: string) =>
		`${project(organizationId, projectId)}/orders/${segment(orderId)}/questions`,
	workOrderQuestionAnswer: (
		organizationId: string,
		projectId: string,
		orderId: string,
		questionId: string,
	) =>
		`${project(organizationId, projectId)}/orders/${segment(orderId)}/questions/${segment(questionId)}/answer`,
	workOrderRecords: (organizationId: string, projectId: string, orderId: string) =>
		`${project(organizationId, projectId)}/orders/${segment(orderId)}/records`,
	workOrderEvidence: (organizationId: string, projectId: string, orderId: string) =>
		`${project(organizationId, projectId)}/orders/${segment(orderId)}/evidence`,
	workOrderSessions: (organizationId: string, projectId: string, orderId: string) =>
		`${project(organizationId, projectId)}/orders/${segment(orderId)}/sessions`,
	workOrderSessionFrames: (
		organizationId: string,
		projectId: string,
		orderId: string,
		sessionId: string,
	) =>
		`${project(organizationId, projectId)}/orders/${segment(orderId)}/sessions/${segment(sessionId)}/frames`,
	workOrderSessionFramesStream: (
		organizationId: string,
		projectId: string,
		orderId: string,
		sessionId: string,
	) =>
		`${project(organizationId, projectId)}/orders/${segment(orderId)}/sessions/${segment(sessionId)}/frames/stream`,
	workOrderSessionInput: (
		organizationId: string,
		projectId: string,
		orderId: string,
		sessionId: string,
	) =>
		`${project(organizationId, projectId)}/orders/${segment(orderId)}/sessions/${segment(sessionId)}/input`,
	workOrderProgress: (organizationId: string, projectId: string, orderId: string) =>
		`${project(organizationId, projectId)}/orders/${segment(orderId)}/progress`,
	workOrderProgressStream: (organizationId: string, projectId: string, orderId: string) =>
		`${project(organizationId, projectId)}/orders/${segment(orderId)}/progress/stream`,
	designSessions: (organizationId: string, projectId: string) =>
		`${project(organizationId, projectId)}/design-sessions`,
	designSessionFrames: (organizationId: string, projectId: string, designSessionId: string) =>
		`${project(organizationId, projectId)}/design-sessions/${segment(designSessionId)}/frames`,
	designSessionFramesStream: (organizationId: string, projectId: string, designSessionId: string) =>
		`${project(organizationId, projectId)}/design-sessions/${segment(designSessionId)}/frames/stream`,
	designSessionInput: (organizationId: string, projectId: string, designSessionId: string) =>
		`${project(organizationId, projectId)}/design-sessions/${segment(designSessionId)}/input`,
} as const;
